import formatIntegerBySpace from '../../../../helpers/formatters/format-integer-by-space'
import { formatRollerDisplay } from '../../../../helpers/format-roller-display'
import Stamp from '../../../../types/api/stamp'
import Task from '../../../../types/api/task'
import {
    formatFormReorderDetailsLabel,
    getFormOrderDetailsFields,
} from '../../../designer-tasks/helpers/form-order-details'
import { OVERTIME_HOUR_RATE } from '../constants/designer-summary-labels'

export interface DesignerTaskRow {
    key: string
    id: number
    type: string
    details: string
    contribution: string
    clientName: string
    shipmentId: number | string
    completedAt: string
}

interface DesignerMonthlyStatsFormatted {
    formsOrdered: string | null
    digitalDismountings: string | null
    stampsOrdered: string | null
    formsTotal: string | null
    flexoBonus: string | null
    flexoCount: string | null
    flexoRate: number | null
    digitalBonus: string | null
    digitalRate: number | null
    overtimeBonus: string | null
    overtimeHours: string | null
    totalBonus: string | null
}

function formatCount(value: number): string | null {
    return value === 0 ? null : String(value)
}

function getFlexoRate(formsTotal: number): number {
    if (formsTotal > 450) return 55
    if (formsTotal > 400) return 50
    if (formsTotal > 300) return 40
    if (formsTotal > 200) return 30
    return 0
}

function getDigitalRate(digitalCount: number): number {
    if (digitalCount > 200) return 80
    if (digitalCount >= 100) return 70
    return 60
}

export function getFormOrderIdsFromTasks(tasks: Task[]): string[] {
    const ids = new Set<string>()

    for (const task of tasks) {
        if (task.type !== 'Заказ форм' && task.type !== 'Перезаказ форм') {
            continue
        }

        const formOrderId = getFormOrderDetailsFields(task).formOrderId

        if (formOrderId) {
            ids.add(formOrderId)
        }
    }

    return Array.from(ids)
}

function getOrderedFormsCount(task: Task, formsCountByOrderId: Map<string, number>): number {
    if (task.type !== 'Заказ форм' && task.type !== 'Перезаказ форм') {
        return 0
    }

    const details = getFormOrderDetailsFields(task)

    if (details.formOrderId && formsCountByOrderId.has(details.formOrderId)) {
        return formsCountByOrderId.get(details.formOrderId) ?? 0
    }

    if (details.forms?.length) {
        return details.forms.length
    }

    if (details.sourceFormIds?.length) {
        return details.sourceFormIds.length
    }

    return task.shipment?.numberOfForms ?? 0
}

function formatStampDetails(stamp?: Stamp): string {
    if (!stamp) return ''

    return [
        `Штамп: ${stamp.number || '—'}`,
        stamp.width != null && stamp.length != null
            ? `Размеры: ${stamp.width}x${stamp.length}`
            : null,
        stamp.roller?.roller != null ? `Вал: ${formatRollerDisplay(stamp.roller.roller)}` : null,
        stamp.floods != null ? `Ручьи: ${stamp.floods}` : null,
        stamp.radius != null ? `Радиус: ${stamp.radius}` : null,
        stamp.range != null ? `М/э: ${stamp.range}` : null,
    ]
        .filter(Boolean)
        .join(', ')
}

function formatTaskDetails(task: Task, formsCountByOrderId: Map<string, number>): string {
    const stampText = formatStampDetails(task.shipment?.stamp)

    switch (task.type) {
        case 'Отрисовка штампа':
            return stampText || '—'
        case 'Отрисовка бланка':
            return task.description?.trim() || '—'
        case 'Заказ форм': {
            const formsCount = getOrderedFormsCount(task, formsCountByOrderId)
            const formsText = formsCount > 0 ? `Количество форм: ${formsCount}` : ''

            return [formsText, stampText].filter(Boolean).join('. ') || '—'
        }
        case 'Перезаказ форм': {
            const reorderLabel = formatFormReorderDetailsLabel(getFormOrderDetailsFields(task))

            return [reorderLabel, stampText].filter(Boolean).join('. ')
        }
        default:
            return task.description?.trim() || '—'
    }
}

function getTaskContributionCount(
    task: Task,
    formsCountByOrderId: Map<string, number>
): number {
    if (task.type === 'Заказ форм' || task.type === 'Перезаказ форм') {
        return getOrderedFormsCount(task, formsCountByOrderId)
    }

    if (task.type === 'Цифровой спуск' || task.type === 'Отрисовка штампа') {
        return 1
    }

    return 0
}

function formatContribution(count: number): string {
    return count > 0 ? `+${count}` : '—'
}

function mapTaskToRow(
    task: Task,
    formsCountByOrderId: Map<string, number> = new Map()
): DesignerTaskRow {
    const contributionCount = getTaskContributionCount(task, formsCountByOrderId)

    return {
        key: task.documentId,
        id: task.id,
        type: task.type,
        details: formatTaskDetails(task, formsCountByOrderId),
        contribution: formatContribution(contributionCount),
        clientName: task.shipment?.client?.name ?? '—',
        shipmentId: task.shipment?.id ?? '—',
        completedAt: task.endDate || task.updatedAt,
    }
}

function calcDesignerMonthlyStats(
    tasks: Task[],
    formsCountByOrderId: Map<string, number> = new Map(),
    overtimeHours = 0
): DesignerMonthlyStatsFormatted {
    let formsOrdered = 0
    let digitalDismountings = 0
    let stampsOrdered = 0

    for (const task of tasks) {
        formsOrdered += getOrderedFormsCount(task, formsCountByOrderId)

        if (task.type === 'Цифровой спуск') {
            digitalDismountings += 1
        }

        if (task.type === 'Отрисовка штампа') {
            stampsOrdered += 1
        }
    }

    const formsTotal = formsOrdered + digitalDismountings + stampsOrdered
    const flexoCount = formsOrdered + stampsOrdered
    const flexoRate = getFlexoRate(formsTotal)
    const flexoBonus = flexoRate * flexoCount
    const digitalRate = getDigitalRate(digitalDismountings)
    const digitalBonus = digitalRate * digitalDismountings
    const overtimeBonus = overtimeHours * OVERTIME_HOUR_RATE
    const totalBonus = flexoBonus + digitalBonus + overtimeBonus

    return {
        formsOrdered: formatCount(formsOrdered),
        digitalDismountings: formatCount(digitalDismountings),
        stampsOrdered: formatCount(stampsOrdered),
        formsTotal: formatCount(formsTotal),
        flexoBonus: formsTotal === 0 ? null : formatIntegerBySpace(flexoBonus),
        flexoCount: formatCount(flexoCount),
        flexoRate: formsTotal === 0 ? null : flexoRate,
        digitalBonus: digitalDismountings === 0 ? null : formatIntegerBySpace(digitalBonus),
        digitalRate: digitalDismountings === 0 ? null : digitalRate,
        overtimeBonus: overtimeBonus === 0 ? null : formatIntegerBySpace(overtimeBonus),
        overtimeHours: overtimeHours === 0 ? null : String(overtimeHours),
        totalBonus: totalBonus === 0 ? null : formatIntegerBySpace(totalBonus),
    }
}

export function buildDesignerMonthlySummary(
    tasks: Task[],
    formsCountByOrderId: Map<string, number>,
    overtimeHours = 0
) {
    return {
        rows: tasks.map(task => mapTaskToRow(task, formsCountByOrderId)),
        stats: calcDesignerMonthlyStats(tasks, formsCountByOrderId, overtimeHours),
    }
}
